import type { AirHandlerProductType } from 'data/types';
import {
  equipmentPlaceholderNote,
  isEquipmentPlaceholderProduct
} from 'utils/equipment-placeholder';
import {
  EquipmentPlaceholderKind,
  equipmentPlaceholderKindOf
} from 'utils/equipment-placeholder-kind';

export type DuctedZoneUnitRow = {
  key: string;
  label: string;
  btu: number;
  photo: string | null;
  type: string;
  placeholderNote: string | null;
  placeholderKind: EquipmentPlaceholderKind | null;
};

export type DuctedZoneUnitsSource = {
  uniqueId: string;
  name: string;
  type: string;
  selectedProduct?: AirHandlerProductType | null;
  blowerProduct?: AirHandlerProductType | null;
};

export function buildZoneUnitRows(
  zone: DuctedZoneUnitsSource
): DuctedZoneUnitRow[] {
  const indoor = zone.selectedProduct;
  const rows: DuctedZoneUnitRow[] = [
    {
      key: `${zone.uniqueId}-indoor`,
      label: zone.name,
      btu: indoor?.coolingBTU ?? 0,
      photo: indoor?.photo ?? null,
      type: zone.type,
      placeholderNote: equipmentPlaceholderNote(indoor),
      placeholderKind: equipmentPlaceholderKindOf(indoor, 'indoor')
    }
  ];
  const blower = zone.blowerProduct;
  if (blower) {
    rows.push({
      key: `${zone.uniqueId}-blower`,
      label: isEquipmentPlaceholderProduct(blower)
        ? blower.name
        : blower.type || 'Blower',
      btu: blower.heatingBTU ?? 0,
      photo: blower.photo ?? null,
      type: zone.type,
      placeholderNote: equipmentPlaceholderNote(blower),
      placeholderKind: equipmentPlaceholderKindOf(blower, 'blower')
    });
  }
  return rows;
}

export function zoneHeatingCapacity(zone: DuctedZoneUnitsSource): number {
  return (
    zone.selectedProduct?.heatingBTU || zone.blowerProduct?.heatingBTU || 0
  );
}

export function abbreviateBtu(btu: number): string {
  if (btu === 0) return '—';
  if (btu >= 1000) {
    return `${(btu / 1000).toFixed(0)}K`;
  }
  return `${btu}`;
}
